import { useEffect, useState } from "react"
import { Container, List, Toggle } from "rsuite"
import CloseIcon from '@rsuite/icons/Close'
import { Button } from "../Controls"
import { useTranslation } from "react-i18next"
import { doQuery } from "../../utils/useQuery"
import UserProjectRelationModel from "@client/models/userProjectRelation"
import RelationName, { RelationType, UserProject } from "./RelationName"
import { Capabilities, hasPermission } from "@/shared"
import { hasRole, RoleType } from "@/shared/roles"

export { RelationType }
export type UserProjectRelationProps = {
    type: RelationType,
    id: string,
    userRole?: RoleType
    buttonText: string,
}

const userProjectRelationModel = new UserProjectRelationModel()

/** How many lines one request brings; the list asks again until it has all. */
const RELATIONS_PER_REQUEST = 50

/** The id of a picked user or project, or the id itself. */
const idOf = (value: any): string => (value && typeof value === 'object' ? String(value._id ?? value.id ?? '') : String(value ?? ''))

const defaultRelation: UserProject = {
    _id: '',
    user: undefined as any,
    project: undefined as any,
    permissions: {
        read: true,
        write: true,
        delete: false,
    }
}
const getDefaultRelation = (type: RelationType, id: string): UserProject => {
    if (type == RelationType.Project)
        return { ...defaultRelation, ...{ user: id as any } }
    /*if (type == RelationType.User)*/
    return { ...defaultRelation, ...{ project: id as any } }
}

export default function UserProjectRelation({ type, id, userRole, buttonText }: UserProjectRelationProps) {
    const { t } = useTranslation()
    const [page, setPage] = useState<number>(1)
    const [relations, setRelations] = useState<UserProject[]>([])
    const [inputValue, setInputValue] = useState<UserProject>(getDefaultRelation(type, id))
    const [editingIndex, setEditingIndex] = useState<number | null>(null)
    const [deletedRelation, setDeletedRelation] = useState<string>('')
    const canWriteAssign = type == 'project' ? hasPermission(Capabilities.project_write_assign_user) : hasPermission(Capabilities.user_write_assign_project)
    const canDeleteAssign = type == 'project' ? hasPermission(Capabilities.project_delete_assign_user) : hasPermission(Capabilities.user_delete_assign_project)

    // Every entry, page after page (operator, 16.10.2026): only the first ten
    // were ever shown — "count" is no parameter of the model, it takes
    // "limit" —, and the rest could be neither seen nor changed nor removed.
    const fetchRelations = async (dropped: () => boolean) => {
        const ownType = type == RelationType.Project ? RelationType.User : RelationType.Project
        let next = 1
        let pageCount = 1
        while (next <= pageCount) {
            const res = await doQuery(userProjectRelationModel.list, { type: ownType, id, page: next, limit: RELATIONS_PER_REQUEST })
            // a load the effect has given up on writes nothing — in development
            // StrictMode starts it twice, and both used to append their lines
            if (dropped()) return
            const data = res.data?.rows || []
            pageCount = res.data?.pageCount || 0
            setRelations((loaded) => [...loaded, ...data])
            next++
            setPage(next)
            if (!data.length) break
        }
    };

    useEffect(() => {
        let dropped = false
        setRelations([])
        fetchRelations(() => dropped)
        return () => {
            dropped = true
        }
    }, [])

    const saveRelation = async (relation: UserProject) => {
        if (!relation.project || !relation.user) return
        // the ids, not the picked records: the server reads an id, and an
        // object arrived as "[object Object]" — every new member was refused
        // with 404 while the line stayed as if saved (operator, 16.10.2026)
        const payload = {
            _id: relation._id,
            user: idOf(relation.user),
            project: idOf(relation.project),
            permissions: relation.permissions,
        }
        const response = (!relation._id
            ? await userProjectRelationModel.create(payload as any)
            : await userProjectRelationModel.update(relation._id, payload as any))
        if (!response?.data) {
            // not stored: a new line goes again, the server's reason is shown
            if (!relation._id) {
                setRelations((items) => items.filter((item) => item._id))
                setEditingIndex(null)
            }
            return
        }

        // Our own line stays, with what the server decided. Its answer names
        // the fields userId/projectId; taking it over whole left the line
        // without user and project, and every later change was dropped
        // without a request.
        setRelations((items) => items.map((item) =>
            item._id == relation._id
                ? { ...relation, _id: response.data._id, permissions: response.data.permissions ?? relation.permissions }
                : item
        ))
    }

    useEffect(() => {
        saveRelation(inputValue)
    }, [inputValue])

    const removeRelation = async () => {
        if (!deletedRelation) return

        const response = await userProjectRelationModel.delete(deletedRelation)
        console.log(response)
        setDeletedRelation('')
    }

    useEffect(() => {
        removeRelation();
    }, [deletedRelation]);

    // for scroll see views/project/in/EditDataList.tsx
    //const handleScroll = (x: number, y: number) => {
    // function handleScroll(event: UIEvent<HTMLElement>): void {
    //     // if (tab?.store?.data?.list?.length == 0) return
    //     // const contextHeight = (tab?.store?.data?.list?.length || 0) * 46;
    //     // const top = Math.abs(y);

    //     // if (contextHeight - top - tableHeight < 300) {
    //     //     setPage(page => page + 1)
    //     //     console.log('scroll: ', page + 1)
    //     // }
    // };

    const addRelation = () => {
        if (editingIndex !== null) {
            const newRelations = [...relations];
            newRelations[editingIndex] = inputValue;
            setRelations(newRelations)
            setEditingIndex(null);
        } else {
            const newRelations = [...relations, inputValue];
            setRelations(newRelations)
            editRelation(relations.length)
        }
        setInputValue(getDefaultRelation(type, id));
    };

    const editRelation = (index: number) => {
        setInputValue(relations[index]);
        setEditingIndex(index);
    };

    const deleteRelation = (index: number) => {
        let deletedItem = ''
        const updated = relations.filter((item, i) => {
            if (i !== index) return true
            deletedItem = item._id
            return false
        })
        setRelations(updated)
        setDeletedRelation(deletedItem)
        setEditingIndex(null)
    };

    const updateInputValue = (value: UserProject, label: string) => {
        setInputValue({ ...value, ...{ label } })
    }

    function toggleCheckbox(permission: string, checked: boolean, index: number): void {
        // a copy: the permissions object is shared with the line in the list
        const line = relations[index]
        setInputValue({ ...line, permissions: { ...line.permissions, [permission]: checked } })
    }

    if (type == 'project' && hasRole(RoleType.Administrator, userRole)) {
        return (
            <span>{t('Assigned to all projects.')}</span>
        )
    }

    return (
        <>
            <List
                key={id}
                className="user-project-relation-list"
            >
                {relations.map((rel, index) => (
                    <List.Item
                        key={index}
                    //onScroll={handleScroll}
                    >
                        <RelationName
                            type={type}
                            relation={editingIndex === index ? inputValue : rel}
                            setInputValue={updateInputValue}

                        />
                        <Toggle
                            size="sm"
                            defaultChecked={rel.permissions.read}
                            checkedChildren={t('Read')}
                            unCheckedChildren={t('Read')}
                            readOnly={!canWriteAssign}
                            onChange={(checked) => toggleCheckbox('read', checked, index)}
                        />
                        <Toggle
                            size="sm"
                            defaultChecked={rel.permissions.write}
                            checkedChildren={t('Write')}
                            unCheckedChildren={t('Write')}
                            readOnly={!canWriteAssign}
                            onChange={(checked) => toggleCheckbox('write', checked, index)}
                        />
                        <Toggle
                            size="sm"
                            defaultChecked={rel.permissions.delete}
                            checkedChildren={t('Delete')}
                            unCheckedChildren={t('Delete')}
                            readOnly={!canWriteAssign}
                            onChange={(checked) => toggleCheckbox('delete', checked, index)}
                        />

                        {canDeleteAssign && <Button className="delete-button rs-btn-close" aria-label={t('Remove')} tabIndex={-1} onClick={() => deleteRelation(index)}><CloseIcon /></Button>}
                    </List.Item>
                ))}
            </List>
            {relations.length == 0 && page <= 1 && <div>{t('Loading ...')}</div>}
            {relations.length == 0 && page != 1 && <div>{type == 'project' ? t('No assigned projects found.') : t('No assigned users found.')}</div>}
            {canWriteAssign && <Container>
                <Button appearance="ghost" onClick={() => { setEditingIndex(null); addRelation() }} style={{ marginTop: '1em', width: 'fit-content' }}>{buttonText}</Button>
            </Container>}
        </>
    )
}